import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  Heading,
  VStack,
  HStack,
  Divider,
  Tag,
  useColorModeValue,
  SimpleGrid,
  Card,
  CardBody,
  Stack,
  Tooltip,
  Button,
  Collapse,
} from '@chakra-ui/react';
import {
  BsClock,
  BsCalendarWeek,
  BsInfoCircle,
  BsMoonStars,
  BsChevronDown,
  BsChevronUp,
} from 'react-icons/bs';
import { AiTwotoneCar as CarIcon } from 'react-icons/ai';
import { InfoIcon } from '@chakra-ui/icons';
import dynamic from 'next/dynamic';

// 顯示週幾的函數
const getDayName = (day) => {
  const days = ['週日', '週一', '週二', '週三', '週四', '週五', '週六'];
  return days[day];
};

// 取得今天是星期幾
const getTodayDayIndex = () => {
  const today = new Date().getDay(); // 0-6 (週日-週六)
  return today;
};

// 取得工作日/假日最高收費，考慮 3600 為無上限的情況
const getMaxRatesByDayType = (parkingRates) => {
  let weekdayMax = 0;
  let weekendMax = 0;
  let hasUnlimitedWeekday = false;
  let hasUnlimitedWeekend = false;

  parkingRates.forEach((rate) => {
    if (rate.day === 0 || rate.day === 6) {
      // 週六、週日
      if (rate.maximum === 3600) {
        hasUnlimitedWeekend = true;
      } else {
        weekendMax = Math.max(weekendMax, rate.maximum);
      }
    } else {
      // 週一至週四
      if (rate.maximum === 3600) {
        hasUnlimitedWeekday = true;
      } else {
        weekdayMax = Math.max(weekdayMax, rate.maximum);
      }
    }
  });

  return {
    weekdayMax,
    weekendMax,
    hasUnlimitedWeekday,
    hasUnlimitedWeekend,
  };
};

// 判斷是否為單一時段（全日收費）
const isSinglePeriod = (parkingRates) => {
  // 檢查是否每天都只有一個時段
  const isSinglePeriod = parkingRates.every(
    (rate) => rate.periods && rate.periods.length === 1,
  );

  return isSinglePeriod;
};

// 判斷是否為跨夜費率 (晚上到早上的費率相同)
const identifyRatePeriods = (parkingRates) => {
  let nightRates = [];
  let dayRates = [];
  let otherRates = [];

  // 如果只有單一時段全日費率，則直接返回
  if (isSinglePeriod(parkingRates)) {
    parkingRates.forEach((rate) => {
      if (rate.periods && rate.periods.length === 1) {
        dayRates.push({
          day: rate.day,
          price: rate.periods[0].price,
          startTime: rate.periods[0].start_time,
          endTime: rate.periods[0].end_time,
        });
      }
    });
    return { nightRates, dayRates, otherRates };
  }

  // 檢查是否有跨夜費率
  parkingRates.forEach((rate) => {
    if (rate.periods && rate.periods.length > 0) {
      const nightEndingPeriods = rate.periods.filter(
        (p) => p.end_time === '23:59',
      );
      const morningStartingPeriods = rate.periods.filter(
        (p) => p.start_time === '00:00',
      );

      // 檢查是否有晚上結束和早上開始的費率相同
      nightEndingPeriods.forEach((nightPeriod) => {
        morningStartingPeriods.forEach((morningPeriod) => {
          if (nightPeriod.price === morningPeriod.price) {
            // 找到跨夜費率
            nightRates.push({
              day: rate.day,
              price: nightPeriod.price,
              startTime: nightPeriod.start_time,
              endTime: morningPeriod.end_time,
            });
          }
        });
      });

      // 收集其他日間費率
      rate.periods.forEach((period) => {
        const isPartOfNightRate = nightRates.some(
          (nr) =>
            nr.day === rate.day &&
            (period.start_time === nr.startTime ||
              period.end_time === nr.endTime),
        );

        if (!isPartOfNightRate) {
          if (period.start_time !== '00:00' && period.end_time !== '23:59') {
            // 典型的日間費率
            dayRates.push({
              day: rate.day,
              price: period.price,
              startTime: period.start_time,
              endTime: period.end_time,
            });
          } else {
            // 其他費率
            otherRates.push({
              day: rate.day,
              price: period.price,
              startTime: period.start_time,
              endTime: period.end_time,
            });
          }
        }
      });
    }
  });

  return { nightRates, dayRates, otherRates };
};

// 獲取平日和假日的價格範圍，排除夜間費率
const getPriceRangeByDayType = (parkingRates) => {
  let weekdayMinPrice = Number.MAX_VALUE;
  let weekdayMaxPrice = 0;
  let weekendMinPrice = Number.MAX_VALUE;
  let weekendMaxPrice = 0;

  // 檢查是否為單一時段
  const singlePeriod = isSinglePeriod(parkingRates);

  if (singlePeriod) {
    // 單一時段處理
    parkingRates.forEach((rate) => {
      if (rate.periods && rate.periods.length === 1) {
        const price = rate.periods[0].price;
        if (rate.day === 0 || rate.day === 6) {
          // 週六、週日
          weekendMinPrice = Math.min(weekendMinPrice, price);
          weekendMaxPrice = Math.max(weekendMaxPrice, price);
        } else {
          // 週一至週四
          weekdayMinPrice = Math.min(weekdayMinPrice, price);
          weekdayMaxPrice = Math.max(weekdayMaxPrice, price);
        }
      }
    });
  } else {
    // 先識別夜間費率
    const { nightRates } = identifyRatePeriods(parkingRates);
    const nightPrices = new Set(nightRates.map((nr) => nr.price));

    parkingRates.forEach((rate) => {
      if (rate.periods && rate.periods.length > 0) {
        rate.periods.forEach((period) => {
          // 檢查是否為夜間費率的一部分
          const isNightRate = isCrossingMidnightPeriod(period, rate.periods);

          // 如果是夜間費率，則跳過不計入日間費率範圍
          if (!isNightRate) {
            if (rate.day === 0 || rate.day === 6) {
              // 週六、週日
              weekendMinPrice = Math.min(weekendMinPrice, period.price);
              weekendMaxPrice = Math.max(weekendMaxPrice, period.price);
            } else {
              // 週一至週五
              weekdayMinPrice = Math.min(weekdayMinPrice, period.price);
              weekdayMaxPrice = Math.max(weekdayMaxPrice, period.price);
            }
          }
        });
      }
    });
  }

  return {
    weekdayPriceRange:
      weekdayMinPrice !== Number.MAX_VALUE
        ? weekdayMinPrice === weekdayMaxPrice
          ? `${weekdayMinPrice}`
          : `${weekdayMinPrice}~${weekdayMaxPrice}`
        : '0',
    weekendPriceRange:
      weekendMinPrice !== Number.MAX_VALUE
        ? weekendMinPrice === weekendMaxPrice
          ? `${weekendMinPrice}`
          : `${weekendMinPrice}~${weekendMaxPrice}`
        : '0',
  };
};

// 取得最便宜和最貴的費率
const getPriceRange = (parkingRates) => {
  let minRate = Number.MAX_VALUE;
  let maxRate = 0;
  let peakHours = '';
  let offPeakHours = '';
  let allPrices = new Set();

  // 檢查是否為單一時段
  const singlePeriod = isSinglePeriod(parkingRates);

  // 單一時段處理
  if (singlePeriod) {
    parkingRates.forEach((rate) => {
      if (rate.periods && rate.periods.length === 1) {
        const price = rate.periods[0].price;
        allPrices.add(price);

        if (price < minRate) {
          minRate = price;
        }
        if (price > maxRate) {
          maxRate = price;
        }
      }
    });

    const priceRange = Array.from(allPrices).sort((a, b) => a - b);
    const priceRangeText =
      priceRange.length > 1
        ? `${Math.min(...priceRange)}~${Math.max(...priceRange)}`
        : `${priceRange[0] || 0}`;

    return {
      minRate: minRate === Number.MAX_VALUE ? 0 : minRate,
      maxRate,
      peakHours: '',
      offPeakHours: '',
      hasNightRate: false,
      nightRates: [],
      dayRates: [],
      priceRange: priceRangeText,
      priceRangeArray: priceRange,
      isSinglePeriod: true,
    };
  }

  const { nightRates, dayRates } = identifyRatePeriods(parkingRates);

  // 檢查所有費率時段
  parkingRates.forEach((rate) => {
    if (rate.periods && rate.periods.length > 0) {
      rate.periods.forEach((period) => {
        allPrices.add(period.price);

        if (period.price < minRate) {
          minRate = period.price;
          offPeakHours = `${period.start_time}-${period.end_time}`;
        }
        if (period.price > maxRate) {
          maxRate = period.price;
          peakHours = `${period.start_time}-${period.end_time}`;
        }
      });
    }
  });

  // 調整顯示，如果發現是夜間費率
  const matchingNightRate = nightRates.find((nr) => nr.price === minRate);
  if (matchingNightRate) {
    offPeakHours = `${matchingNightRate.startTime}-${matchingNightRate.endTime}(跨夜)`;
  }

  const matchingPeakRate = dayRates.find((dr) => dr.price === maxRate);
  if (matchingPeakRate) {
    peakHours = `${matchingPeakRate.startTime}-${matchingPeakRate.endTime}`;
  }

  // 價格範圍
  const priceRange = Array.from(allPrices).sort((a, b) => a - b);
  const priceRangeText =
    priceRange.length > 1
      ? `${Math.min(...priceRange)}~${Math.max(...priceRange)}`
      : `${priceRange[0] || 0}`;

  return {
    minRate: minRate === Number.MAX_VALUE ? 0 : minRate,
    maxRate,
    peakHours,
    offPeakHours,
    hasNightRate: nightRates.length > 0,
    nightRates,
    dayRates,
    priceRange: priceRangeText,
    priceRangeArray: priceRange,
    isSinglePeriod: false,
  };
};

// 判斷是否為跨夜費率的函數
const isCrossingMidnightPeriod = (period, allPeriods) => {
  if (period.end_time === '23:59') {
    // 檢查是否存在從 00:00 開始且費率相同的時段
    return allPeriods.some(
      (p) => p.start_time === '00:00' && p.price === period.price,
    );
  }
  if (period.start_time === '00:00') {
    // 檢查是否存在到 23:59 結束且費率相同的時段
    return allPeriods.some(
      (p) => p.end_time === '23:59' && p.price === period.price,
    );
  }
  return false;
};

// 費率區間元件
const PeriodItem = ({ period, isCrossingMidnight }) => {
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Box
      p={2}
      my={1}
      bg={bgColor}
      borderRadius='md'
      borderWidth='1px'
      borderColor={borderColor}>
      <HStack justify='space-between'>
        <HStack>
          <Text fontSize='sm'>
            {period.start_time} - {period.end_time}
          </Text>
        </HStack>
        <Badge colorScheme='gray' fontSize='sm'>
          ${period.price}/小時
        </Badge>
      </HStack>
    </Box>
  );
};

// 價格摘要元件 - 增加 SEO 友好的內容
const PriceSummary = ({ parkingRates, buildingName, spaceNumber }) => {
  const { weekdayMax, weekendMax, hasUnlimitedWeekday, hasUnlimitedWeekend } =
    getMaxRatesByDayType(parkingRates);

  const {
    minRate,
    maxRate,
    peakHours,
    offPeakHours,
    hasNightRate,
    nightRates,
    dayRates,
    priceRange,
    isSinglePeriod,
  } = getPriceRange(parkingRates);

  const { weekdayPriceRange, weekendPriceRange } =
    getPriceRangeByDayType(parkingRates);

  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.600');

  // 組合停車場描述文字，考慮各種情況
  let parkingDescription = `${buildingName}停車場提供彈性的停車方案。`;

  // 加入上限描述
  if (hasUnlimitedWeekday && hasUnlimitedWeekend) {
    parkingDescription += '本停車場無每日收費上限，依時段計費。';
  } else {
    if (!hasUnlimitedWeekday) {
      parkingDescription += `平日停車最高收費為${weekdayMax}元，`;
    } else {
      parkingDescription += '平日停車無收費上限，';
    }

    if (!hasUnlimitedWeekend) {
      parkingDescription += `假日最高收費為${weekendMax}元。`;
    } else {
      parkingDescription += '假日停車無收費上限。';
    }
  }

  // 檢查是否為單一時段收費
  if (isSinglePeriod) {
    parkingDescription += `本停車場全天採用單一費率每小時${priceRange}元。`;
  } else {
    // 加入費率描述
    parkingDescription += `一般停車費率每小時${priceRange}元。`;

    // 加入平假日差異和夜間費率描述（整合顯示）
    if (hasNightRate && nightRates.length > 0) {
      const uniqueNightRates = [...new Set(nightRates.map((nr) => nr.price))];

      if (weekdayPriceRange !== weekendPriceRange) {
        if (uniqueNightRates.length === 1) {
          parkingDescription += `平日白天每小時${weekdayPriceRange}元，假日白天每小時${weekendPriceRange}元，夜間每小時${uniqueNightRates[0]}元。`;
        } else {
          parkingDescription += `平日白天每小時${weekdayPriceRange}元，假日白天每小時${weekendPriceRange}元，另提供多種夜間費率方案。`;
        }
      } else {
        if (uniqueNightRates.length === 1) {
          parkingDescription += `白天每小時${weekdayPriceRange}元，夜間每小時${uniqueNightRates[0]}元。`;
        } else {
          parkingDescription += `白天每小時${weekdayPriceRange}元，夜間提供不同費率方案。`;
        }
      }
    } else {
      // 無夜間費率，只顯示平假日差異
      if (weekdayPriceRange !== weekendPriceRange) {
        parkingDescription += `平日每小時${weekdayPriceRange}元，假日每小時${weekendPriceRange}元。`;
      }
    }
  }

  parkingDescription += '本停車場採時段計費，適合任何停車需求。';

  return (
    <Box
      as='section'
      aria-labelledby='price-summary-heading'
      mb={6}
      p={4}
      bg={bgColor}
      borderRadius='md'>
      <Heading as='h2' id='price-summary-heading' size='md' mb={2}>
        {buildingName} 停車費率與車位資訊摘要
      </Heading>

      <Text fontSize='sm' color='red.500' mb={4} fontWeight={500}>
        * 以下價格與車位為參考資訊，實際費率以現場停車場公告為準。
      </Text>

      <Card bg={cardBg} mb='4'>
        <CardBody>
          <HStack spacing={3}>
            <CarIcon />
            <Heading as='h3' size='sm'>
              總車位數
            </Heading>
            <Text fontWeight='bold' fontSize='20px' flex='1' textAlign='right'>
              {spaceNumber}
            </Text>
          </HStack>
        </CardBody>
      </Card>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <Card bg={cardBg}>
          <CardBody>
            <HStack spacing={3} mb={2}>
              <BsCalendarWeek />
              <Heading as='h3' size='sm'>
                平日/假日收費上限
              </Heading>
            </HStack>
            <Divider mb={3} />
            <Stack>
              <HStack justify='space-between'>
                <Text>平日最高收費</Text>
                {hasUnlimitedWeekday ? (
                  <HStack>
                    <InfoIcon color='gray.400' />
                    <Text>無上限</Text>
                  </HStack>
                ) : (
                  <Badge colorScheme='gray' fontSize='md'>
                    ${weekdayMax}
                  </Badge>
                )}
              </HStack>
              <HStack justify='space-between'>
                <Text>假日最高收費</Text>
                {hasUnlimitedWeekend ? (
                  <HStack>
                    <InfoIcon color='gray.400' />
                    <Text>無上限</Text>
                  </HStack>
                ) : (
                  <Badge colorScheme='gray' fontSize='md'>
                    ${weekendMax}
                  </Badge>
                )}
              </HStack>
            </Stack>
          </CardBody>
        </Card>

        <Card bg={cardBg}>
          <CardBody>
            <HStack spacing={3} mb={2}>
              <BsClock />
              <Heading as='h3' size='sm'>
                時段費率範圍
              </Heading>
            </HStack>
            <Divider mb={3} />
            <Stack>
              {isSinglePeriod ? (
                <HStack justify='space-between'>
                  <Text>單一費率</Text>
                  <Badge colorScheme='blue' fontSize='md'>
                    ${priceRange}/小時
                  </Badge>
                </HStack>
              ) : (
                <>
                  {weekdayPriceRange !== '0' && (
                    <HStack justify='space-between'>
                      <Text>平日費率</Text>
                      <Badge colorScheme='white' fontSize='md'>
                        ${weekdayPriceRange}/小時
                      </Badge>
                    </HStack>
                  )}
                  {weekendPriceRange !== '0' && (
                    <HStack justify='space-between' mt={1}>
                      <Text>假日費率</Text>
                      <Badge colorScheme='white' fontSize='md'>
                        ${weekendPriceRange}/小時
                      </Badge>
                    </HStack>
                  )}
                  {hasNightRate && (
                    <HStack justify='space-between' mt={1}>
                      <HStack>
                        <Text>夜間費率</Text>
                        <Tooltip label='夜間跨午夜時段' hasArrow>
                          <Box
                            as={BsMoonStars}
                            size='14px'
                            color='purple.500'
                          />
                        </Tooltip>
                      </HStack>
                      <Badge colorScheme='purple' fontSize='md'>
                        ${Math.min(...nightRates.map((nr) => nr.price))}/小時
                      </Badge>
                    </HStack>
                  )}
                </>
              )}
            </Stack>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* 增加 SEO 友好的描述性文字 */}
      <Card
        bg={cardBg}
        as='section'
        aria-label='停車場費率詳細描述'
        mt={4}
        borderRadius='md'>
        <CardBody>
          <HStack mb={2}>
            <BsInfoCircle />
            <Heading as='h3' size='sm'>
              費率資訊
            </Heading>
          </HStack>
          <Divider mb={3} />
          <Text fontSize='sm'>{parkingDescription}</Text>
        </CardBody>
      </Card>
    </Box>
  );
};

// 使用 dynamic import 解決 hydration 問題
// 包裝表格部分為客戶端專用組件
const RatesTable = ({
  parkingRates,
  todayDayIndex,
  showAllDays,
  setShowAllDays,
  headerBg,
  todayBg,
  weekendBg,
  buttonBg,
}) => {
  // 將費率按照今天、其他日子分組
  const rates = parkingRates.sort((a, b) => {
    // 我們希望順序是 今天 (todayDayIndex), 明天, 後天 ... 以此類推，
    const adjustedDayA = a.day < todayDayIndex ? a.day + 7 : a.day;
    const adjustedDayB = b.day < todayDayIndex ? b.day + 7 : b.day;
    return adjustedDayA - adjustedDayB;
  });

  return (
    <>
      <Box width='100%' overflowX='auto'>
        <Table variant='simple' size='sm' width='100%'>
          <Thead>
            <Tr bg={headerBg}>
              <Th width='15%'>日期</Th>
              <Th width='25%'>最高收費(元/日)</Th>
              <Th width='60%'>計費區間</Th>
            </Tr>
          </Thead>
          <Tbody>
            {/* 顯示所有日期的費率，但只顯示第一個（今天） */}
            {rates.slice(0, 1).map((rate, index) => (
              <Tr key={index} bg={todayBg}>
                <Td>
                  <Stack align='center'>
                    <Text fontWeight='bold'>{getDayName(rate.day)}</Text>
                    <Text
                      fontWeight='bold'
                      fontSize="12px"
                      px="4px"
                      py="4px"
                      bg="green.500"
                      color="white"
                      borderRadius="md"
                      lineHeight="1.1"
                      textAlign="center"
                    >今天</Text>
                  </Stack>
                </Td>
                <Td>
                  {rate.maximum === 3600 ? (
                    <HStack>
                      <InfoIcon color='gray.400' />
                      <Text>無上限</Text>
                    </HStack>
                  ) : (
                    <Badge colorScheme='gray' fontSize='md'>
                      ${rate.maximum}
                    </Badge>
                  )}
                </Td>
                <Td>
                  <VStack align='stretch' spacing={2} width='100%'>
                    {rate.periods && rate.periods.length > 0 ? (
                      rate.periods.map((period, periodIndex) => (
                        <PeriodItem
                          key={periodIndex}
                          period={period}
                          isCrossingMidnight={isCrossingMidnightPeriod(
                            period,
                            rate.periods,
                          )}
                        />
                      ))
                    ) : (
                      <Text fontSize='sm' color='gray.500'>
                        無特定時段費率
                      </Text>
                    )}
                  </VStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        {/* 其他日子的費率 - 使用折疊面板 */}
        <Collapse in={showAllDays} animateOpacity>
          <Table variant='simple' size='sm' width='100%'>
            <Tbody>
              {rates.slice(1).map((rate, index) => (
                <Tr
                  key={index}
                  bg={rate.day === 0 || rate.day === 6 ? weekendBg : undefined}>
                  <Td width='15%'>
                    <Stack align='center'>
                      <Text fontWeight='bold'>{getDayName(rate.day)}</Text>
                    </Stack>
                  </Td>
                  <Td width='25%'>
                    {rate.maximum === 3600 ? (
                      <HStack>
                        <InfoIcon color='gray.400' />
                        <Text>無上限</Text>
                      </HStack>
                    ) : (
                      <Badge colorScheme='gray' fontSize='md'>
                        ${rate.maximum}
                      </Badge>
                    )}
                  </Td>
                  <Td width='60%'>
                    <VStack align='stretch' spacing={2} width='100%'>
                      {rate.periods && rate.periods.length > 0 ? (
                        rate.periods.map((period, periodIndex) => (
                          <PeriodItem
                            key={periodIndex}
                            period={period}
                            isCrossingMidnight={isCrossingMidnightPeriod(
                              period,
                              rate.periods,
                            )}
                          />
                        ))
                      ) : (
                        <Text fontSize='sm' color='gray.500'>
                          無特定時段費率
                        </Text>
                      )}
                    </VStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Collapse>
      </Box>

      {/* 查看更多按鈕 */}
      <Button
        onClick={() => setShowAllDays(!showAllDays)}
        size='sm'
        variant='outline'
        leftIcon={showAllDays ? <BsChevronUp /> : <BsChevronDown />}
        my={3}
        width='100%'
        bg={buttonBg}>
        {showAllDays ? '收起其他日期費率' : '查看其他日期費率'}
      </Button>
    </>
  );
};

// 動態導入表格組件 - 僅客戶端渲染
// 這將解決 hydration 不匹配的問題
const DynamicRatesTable = dynamic(() => Promise.resolve(RatesTable), {
  ssr: false,
  loading: () => <Text>載入中...</Text>,
});

// 主元件
const ParkingRatesDisplay = ({
  parkingRates,
  buildingName = '本停車場',
  spaceNumber = '暫不提供',
}) => {
  const [showAllDays, setShowAllDays] = useState(false);
  const [todayDayIndex, setTodayDayIndex] = useState(1); // 預設星期一

  // 初始化時獲取今天的日期
  useEffect(() => {
    setTodayDayIndex(getTodayDayIndex());
  }, []);

  const headerBg = useColorModeValue('gray.100', 'gray.700');
  const weekendBg = useColorModeValue('orange.50', 'orange.900');
  const todayBg = useColorModeValue('green.50', 'green.900');
  const buttonBg = useColorModeValue('white', 'gray.100');

  // 所有費率都顯示出來，確保SEO能索引
  const allRatesHTML = (
    <>
      {parkingRates.map((rate, index) => (
        <div key={index} style={{ display: 'none' }} aria-hidden='true'>
          <h3>{getDayName(rate.day)}停車費率</h3>
          <h3>車位總格數：{spaceNumber}</h3>
          <p>
            最高收費: {rate.maximum === 3600 ? '無上限' : `${rate.maximum}元`}
          </p>
          {rate.periods &&
            rate.periods.map((period, i) => (
              <p key={i}>
                {period.start_time}-{period.end_time}: {period.price}元/小時
              </p>
            ))}
        </div>
      ))}
    </>
  );

  return (
    <div itemScope itemType='https://schema.org/ParkingFacility'>
      {/* 隱藏的 schema.org 標記，增強 SEO */}
      <meta itemProp='name' content={buildingName} />
      <meta
        itemProp='description'
        content={`${buildingName}停車費率資訊，提供每日及時段性計費方案。`}
      />

      {/* 隱藏的完整內容，供 SEO 索引用 */}
      {allRatesHTML}

      {/* 價格摘要區塊 */}
      <PriceSummary
        parkingRates={parkingRates}
        buildingName={buildingName}
        spaceNumber={spaceNumber}
      />

      <Heading as='h2' size='md' mb={4}>
        詳細費率表
      </Heading>

      {/* 使用動態導入的表格組件，完全在客戶端渲染 */}
      <DynamicRatesTable
        parkingRates={parkingRates}
        todayDayIndex={todayDayIndex}
        showAllDays={showAllDays}
        setShowAllDays={setShowAllDays}
        headerBg={headerBg}
        todayBg={todayBg}
        weekendBg={weekendBg}
        buttonBg={buttonBg}
      />

      <Divider my={2} />

      <Box mt={2} pb='30px'>
        <Text fontSize='sm' color='gray.500' mt={2} textAlign='center'>
          * 費率/車格可能會依照節假日及特殊情況有所調整，詳情請參考現場公告
        </Text>
      </Box>
    </div>
  );
};

export default ParkingRatesDisplay;
